/**
 * arbi-dev Engine Editor — Engine Editor V1 Day 1 Foundation.
 *
 * Write-altitude edit affordances over ISG's configured engine state.
 * Companion to Engine Atlas (read-altitude inspection at /admin/engine-atlas).
 * Admin-only via top-level AdminGate per ADR 37.
 *
 * Scope reference: docs/engine-editor-v1-scope.md (Sol-locked 407f0ac;
 * cleanup α-amendment b2e2496).
 * Build plan: docs/engine-editor-v1-build-plan.md.
 *
 * Architecture per scope-doc §2 α-amendment (post-cleanup 2026-05-28):
 * Atlas linear-page inheritance with dual-container composition. Title
 * section at max-w-5xl (h1 + TitleBlock + engine/scenario boundary note +
 * write-mode boundary acknowledgment). Grid section at max-w-7xl (3×3
 * grid with axis labels + cells). DOM anchored sections (#weight-components,
 * #buy-score-composition, #map-enforcement + cluster-altitude wrappers)
 * provide cross-link target stability for Atlas migration at Day 3 P4.
 *
 * Q1 verbatim Atlas inheritance per scope-doc §2 line 207-216:
 *   - Atlas geography (3×3 grid coordinates + cell occupancy) preserved
 *   - Empty-cell labels Atlas §3 verbatim
 *   - Reused content-agnostic primitives: AtlasGrid, AxisLabel,
 *     PositionRowCell, EmptyCellLabel, TitleBlock, BoundaryNote
 *   - Engine Editor cells: own scaffold (own components per Item 6 prefix
 *     convention) under apps/arbi-dev/src/components/engine-editor/
 *
 * Day 1 P5 sub-shape negative-affordance rendering substrate-wide:
 *   - 4 cells render 4a positive affordance at Day 2 (Buy Score composition
 *     + Weight components) + Day 3 (MAP enforcement + Per-brand enforces_map)
 *   - 10 other cells render negative-affordance content per scope-doc §4.2
 *     verbatim copy framing across sub-shapes 1a / 1b / 2 / 3a / 3b / 3c /
 *     4b plain / 4b-deferred
 *
 * ReadingThisMap question filed for separate scope-doc clarification commit
 * per Sol direction (cleanup commit b2e2496). Day 1 omits ReadingThisMap;
 * scope-doc §2 Layout enumeration does not list it.
 */

import type { JSX } from 'react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

// Reused Atlas primitives (content-agnostic per scope-doc §3.2 copy principle)
import TitleBlock from '../components/atlas/TitleBlock';
import BoundaryNote from '../components/atlas/BoundaryNote';
import EmptyCellLabel from '../components/atlas/EmptyCellLabel';
import AtlasGrid from '../components/atlas/AtlasGrid';
import AxisLabel from '../components/atlas/AxisLabel';
import PositionRowCell from '../components/atlas/PositionRowCell';

// Engine Editor-specific components (write-altitude per Item 6 prefix convention)
import EngineEditorWriteModeBoundaryNote from '../components/engine-editor/EngineEditorWriteModeBoundaryNote';
import EngineEditorDecisionRulesCluster from '../components/engine-editor/clusters/EngineEditorDecisionRulesCluster';
import EngineEditorPerEntityCluster from '../components/engine-editor/clusters/EngineEditorPerEntityCluster';
import EngineEditorEngineCalculationsRegion from '../components/engine-editor/regions/EngineEditorEngineCalculationsRegion';
import EngineEditorBusinessDefaults from '../components/engine-editor/regions/EngineEditorBusinessDefaults';
import EngineEditorPathMechanics from '../components/engine-editor/regions/EngineEditorPathMechanics';
import EngineEditorWeightComponents from '../components/engine-editor/regions/EngineEditorWeightComponents';

import { URL_DEV_ENGINE_ATLAS } from '../lib/engine-editor/engineEditorUrls';

// Empty-cell copy verbatim from docs/engine-atlas-v1-scope.md §3 / EngineAtlas.tsx
// const definitions (Q1 verbatim inheritance per scope-doc §2 line 215).
const EMPTY_CELL_SUBSTRATE_DECISION_COPY =
  'ISG’s decision rules are currently encoded in engine calculations, not as configurable data. Per-entity rule overrides as data is future scope.';
const EMPTY_CELL_TRANSFORMATION_INPUT_COPY =
  'No standalone input-stage transformations. The engine reads substrate directly into its calculations.';

export default function EngineEditor(): JSX.Element {
  useDocumentTitle('Engine Editor');

  return (
    <div className="h-full overflow-auto">
      {/* ── Title + legend section (max-w-5xl per Atlas inheritance) ─────── */}
      <div className="max-w-5xl mx-auto p-5 space-y-6">
        <div className="flex items-baseline justify-between gap-4">
          <h1 className="text-xl font-semibold text-ink-bright">Engine Editor</h1>
          <a
            href={URL_DEV_ENGINE_ATLAS}
            className="text-xs text-accent hover:underline whitespace-nowrap"
          >
            View as Atlas →
          </a>
        </div>

        <TitleBlock />

        <BoundaryNote />
        <EngineEditorWriteModeBoundaryNote />
      </div>

      {/* ── Grid section (max-w-7xl per Atlas Day 2 visual-layout (a)) ──── */}
      <div className="max-w-7xl mx-auto px-5 pb-5 space-y-3">
        {/* COMPOSABILITY: top-strip slot for compressed change-history block — wires post-Day 92 */}
        {/* COMPOSABILITY: Engine-Editor-specific reserved slot for admin-write change history — wires post-Day 92 */}

        <AtlasGrid>
          {/* Row 1 — axis labels (top margin: stage labels) */}
          <div aria-hidden="true" />
          <AxisLabel axis="horizontal" label="input" />
          <AxisLabel axis="horizontal" label="calculation" />
          <AxisLabel axis="horizontal" label="decision" />

          {/* Row 2 — position altitude (quiet per E.1; no map content) */}
          <AxisLabel axis="vertical" label="position" />
          <PositionRowCell cellCoordinate="position-input" />
          <PositionRowCell cellCoordinate="position-calculation" />
          <PositionRowCell cellCoordinate="position-decision" />

          {/* Row 3 — transformation altitude */}
          <AxisLabel axis="vertical" label="transformation" />
          <EmptyCellLabel
            cellCoordinate="transformation-input"
            copy={EMPTY_CELL_TRANSFORMATION_INPUT_COPY}
          />
          <EngineEditorEngineCalculationsRegion />
          <EngineEditorDecisionRulesCluster />

          {/* Row 4 — substrate altitude */}
          <AxisLabel axis="vertical" label="substrate" />
          <EngineEditorPerEntityCluster />
          {/* Substrate × calculation: ONE cell containing 3 flat regions per
              scope §3.1 cluster principle (flat-regions mode) */}
          <div className="space-y-2">
            <EngineEditorBusinessDefaults />
            <EngineEditorPathMechanics />
            <EngineEditorWeightComponents />
          </div>
          <EmptyCellLabel
            cellCoordinate="substrate-decision"
            copy={EMPTY_CELL_SUBSTRATE_DECISION_COPY}
          />
        </AtlasGrid>
      </div>
    </div>
  );
}
